import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  LocalPolice as LocalPoliceIcon,
  VolunteerActivism as VolunteerActivismIcon,
  Phone as PhoneIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { SafetyContactsLookupResponse } from '../services/safetyContactsService';

// FEAT-044 — Local Safety Contacts (MVP-8.10a, MVP-18.31).
// Calm, never alarming. Renders click-to-dial `tel:` links with explicit
// `aria-label="Call <number>"` so screen readers announce the action
// distinctly from the surrounding label (per §15 a11y).

interface SafetyContactsPanelProps {
  zip?: string;
  result?: SafetyContactsLookupResponse | null;
  loading?: boolean;
  error?: string | null;
}

function buildTelHref(rawNumber: string): string {
  const digits = rawNumber.replace(/[^\d+]/g, '');
  return `tel:${digits}`;
}

function formatRelative(updatedAt: string): string {
  try {
    const date = new Date(updatedAt);
    if (Number.isNaN(date.getTime())) return '';
    return `Updated ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  } catch {
    return '';
  }
}

export const SafetyContactsPanel: React.FC<SafetyContactsPanelProps> = ({
  zip,
  result,
  loading = false,
  error = null,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card sx={{ borderRadius: 2 }} aria-busy="true" data-testid="safety-contacts-panel-loading">
        <CardContent>
          <Typography variant="body1" color="text.secondary">
            Looking up local contacts…
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ borderRadius: 2 }} data-testid="safety-contacts-panel-error">
        <CardContent>
          <Typography variant="body1" color="text.primary">
            We couldn't load local contacts just now.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please try again in a moment. The national support line below is always available.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Fallback />
        </CardContent>
      </Card>
    );
  }

  if (!result) {
    return (
      <Card sx={{ borderRadius: 2 }} data-testid="safety-contacts-panel-empty">
        <CardContent>
          <Typography variant="body1" color="text.secondary">
            Enter a US zip code to see local non-emergency contacts for your area.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (result.fallback) {
    return (
      <Card sx={{ borderRadius: 2 }} data-testid="safety-contacts-panel-fallback">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoOutlinedIcon color="primary" aria-hidden />
            <Typography variant="h6" component="h2">
              Local contacts coming soon
            </Typography>
          </Stack>
          <Typography variant="body1" color="text.primary" sx={{ mt: 1 }}>
            We don't have local non-emergency numbers for {zip ? `zip ${zip}` : 'this zip'} yet.
            In the meantime, United Way's national support line can connect you to nearby
            community services.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Fallback />
        </CardContent>
      </Card>
    );
  }

  const { contact } = result;

  return (
    <Card sx={{ borderRadius: 2 }} data-testid="safety-contacts-panel-hit">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center">
          <PhoneIcon color="primary" aria-hidden />
          <Typography variant="h6" component="h2">
            Local contacts for zip {contact.zip}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          These are non-emergency lines. For a medical emergency, please call 911.
        </Typography>

        <List sx={{ mt: 2 }}>
          <ListItem disableGutters data-testid="safety-contact-non-emergency-police">
            <ListItemIcon>
              <LocalPoliceIcon
                aria-hidden
                sx={{ color: theme.palette.primary.main }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Non-emergency police"
              secondary={
                <Box
                  component="a"
                  href={buildTelHref(contact.nonEmergencyPolice)}
                  aria-label={`Call ${contact.nonEmergencyPolice}`}
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover, &:focus': { textDecoration: 'underline' },
                  }}
                >
                  {contact.nonEmergencyPolice}
                </Box>
              }
            />
          </ListItem>
          <ListItem disableGutters data-testid="safety-contact-aps">
            <ListItemIcon>
              <VolunteerActivismIcon
                aria-hidden
                sx={{ color: theme.palette.primary.main }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Adult Protective Services"
              secondary={
                <Box
                  component="a"
                  href={buildTelHref(contact.aps)}
                  aria-label={`Call ${contact.aps}`}
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover, &:focus': { textDecoration: 'underline' },
                  }}
                >
                  {contact.aps}
                </Box>
              }
            />
          </ListItem>
        </List>

        {contact.regionalNotes ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {contact.regionalNotes}
            </Typography>
          </>
        ) : null}

        {contact.updatedAt ? (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 2, display: 'block' }}>
            {formatRelative(contact.updatedAt)}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
};

const Fallback: React.FC = () => {
  const theme = useTheme();
  return (
    <List dense data-testid="safety-contacts-fallback">
      <ListItem disableGutters>
        <ListItemIcon>
          <PhoneIcon aria-hidden sx={{ color: theme.palette.primary.main }} />
        </ListItemIcon>
        <ListItemText
          primary="United Way community support"
          secondary={
            <Box
              component="a"
              href="tel:211"
              aria-label="Call 211"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover, &:focus': { textDecoration: 'underline' },
              }}
            >
              211
            </Box>
          }
        />
      </ListItem>
    </List>
  );
};

export default SafetyContactsPanel;
